import { QueryDocumentSnapshot } from 'firebase/firestore';
import { Incident, IncidentType, IncidentStatus, IncidentPriority } from '@/types/community/incident';
import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  setDoc
} from 'firebase/firestore';
import { addWorkspaceAndActivity, createActivityRecord, isDemoWorkspace } from '@/utils/apiHelpers';
import { User } from '../types/users';
import { formatFirestoreTimestamp } from '@/utils/date';
import { removeUndefined } from '@/utils/removeUndefined';
import { loadMockData } from '@/mock';

// Helper functions for mock data
function filterMockData<T extends { title?: string; description?: string; type?: string; priority?: string; status?: string; reportedBy?: { id?: string }; assignedTo?: { id?: string }; block?: { id?: string }; apartmentUnit?: { id?: string }; amenity?: { id?: string } }>(
  data: T[],
  searchTerm?: string,
  type?: string,
  priority?: string,
  status?: string,
  reportedBy?: string,
  assignedTo?: string,
  blockId?: string,
  apartmentUnitId?: string,
  amenityId?: string
): T[] {
  return data.filter(item => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        (item.title && item.title.toLowerCase().includes(searchLower)) ||
        (item.description && item.description.toLowerCase().includes(searchLower));
      if (!matchesSearch) return false;
    }
    
    if (type && item.type !== type) return false;
    if (priority && item.priority !== priority) return false;
    if (status && item.status !== status) return false;
    if (reportedBy && item.reportedBy?.id !== reportedBy) return false;
    if (assignedTo && item.assignedTo?.id !== assignedTo) return false;
    if (blockId && item.block?.id !== blockId) return false;
    if (apartmentUnitId && item.apartmentUnit?.id !== apartmentUnitId) return false;
    if (amenityId && item.amenity?.id !== amenityId) return false;
    
    return true;
  });
}

function paginateMockData<T>(
  data: T[],
  pageSize?: number,
  currentPage: number = 1
): { data: T[]; total: number } {
  if (!pageSize) {
    return { data, total: data.length };
  }
  
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = data.slice(startIndex, endIndex);
  
  return {
    data: paginatedData,
    total: data.length
  };
}

// Constants
const INCIDENTS_COLLECTION = 'incidents';

// Types
export interface IncidentsQueryParams {
  searchTerm?: string;
  type?: IncidentType;
  priority?: IncidentPriority;
  status?: IncidentStatus;
  reportedBy?: string;
  assignedTo?: string;
  blockId?: string;
  apartmentUnitId?: string;
  amenityId?: string;
  pageSize?: number;
  page?: number;
  lastDoc?: QueryDocumentSnapshot;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  firestoreUser: User;
}

type IncidentWithDates = Partial<Incident>;

// Incident API
export async function getIncidents({
  searchTerm,
  type,
  status,
  priority,
  reportedBy,
  assignedTo,
  blockId,
  apartmentUnitId,
  amenityId,
  pageSize,
  page = 1,
  sortBy,
  sortOrder,
  firestoreUser
}: IncidentsQueryParams): Promise<{ incidents: Incident[]; lastDoc?: QueryDocumentSnapshot; total: number }> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log('DEMO MODE: Fetching mock incidents with filters', { searchTerm, type, status, priority, page, pageSize });
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Use imported mock data
    const mockIncidents = await loadMockData<Incident[]>('incidents', firestoreUser.language);
    const filteredData = filterMockData(mockIncidents, searchTerm, type, status, priority, blockId, apartmentUnitId);
    const paginatedData = paginateMockData(filteredData, pageSize, page);
    
    return {
      incidents: paginatedData.data,
      lastDoc: undefined, // Mock data doesn't have Firestore documents
      total: paginatedData.total
    };
  }
  
  console.log('API Call: getIncidents', { firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const incidentsRef = collection(db, INCIDENTS_COLLECTION);
    const q = query(
      incidentsRef,
      where('workspace.id', '==', firestoreUser.workspace.id)
    );

    // Remove complex queries - only filter by workspace
    // All other filtering, sorting, and pagination will be done client-side

    const snapshot = await getDocs(q);
    const incidents = snapshot.docs
      .map(doc => {
        const data = doc.data() as IncidentWithDates;
        const mapped: IncidentWithDates = {
          ...data,
          id: doc.id,
          reportedAt: data.reportedAt ? formatFirestoreTimestamp(data.reportedAt) ?? undefined : undefined,
          resolvedAt: data.resolvedAt ? formatFirestoreTimestamp(data.resolvedAt) ?? undefined : undefined
        };
        // Only return if all required fields are present
        if (
          typeof mapped.title === 'string' &&
          typeof mapped.description === 'string' &&
          typeof mapped.type === 'string' &&
          typeof mapped.priority === 'string' &&
          typeof mapped.status === 'string' &&
          mapped.reportedBy && typeof mapped.reportedBy.id === 'string' &&
          mapped.reportedAt instanceof Date
        ) {
          return mapped as Incident;
        }
        return undefined;
      })
      .filter((i): i is Incident => !!i);

    // Apply all filters client-side
    let filteredIncidents = incidents;

    // Apply search filter
    if (searchTerm) {
      filteredIncidents = filteredIncidents.filter(incident => 
        incident.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        incident.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        incident.reportedBy.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply type filter
    if (type) {
      filteredIncidents = filteredIncidents.filter(incident => incident.type === type);
    }

    // Apply priority filter
    if (priority) {
      filteredIncidents = filteredIncidents.filter(incident => incident.priority === priority);
    }

    // Apply status filter
    if (status) {
      filteredIncidents = filteredIncidents.filter(incident => incident.status === status);
    }

    // Apply reportedBy filter
    if (reportedBy) {
      filteredIncidents = filteredIncidents.filter(incident => incident.reportedBy.id === reportedBy);
    }

    // Apply assignedTo filter
    if (assignedTo) {
      filteredIncidents = filteredIncidents.filter(incident => incident.assignedTo?.id === assignedTo);
    }

    // Apply block filter
    if (blockId) {
      filteredIncidents = filteredIncidents.filter(incident => incident.block?.id === blockId);
    }

    // Apply apartment unit filter
    if (apartmentUnitId) {
      filteredIncidents = filteredIncidents.filter(incident => incident.apartmentUnit?.id === apartmentUnitId);
    }

    // Apply amenity filter
    if (amenityId) {
      filteredIncidents = filteredIncidents.filter(incident => incident.amenity?.id === amenityId);
    }

    // Apply sorting client-side
    if (sortBy) {
      filteredIncidents.sort((a, b) => {
        const aValue = (a as any)[sortBy];
        const bValue = (b as any)[sortBy];
        
        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    } else {
      // Default sort by reportedAt desc
      filteredIncidents.sort((a, b) => b.reportedAt.getTime() - a.reportedAt.getTime());
    }

    // Apply pagination client-side
    let paginatedIncidents = filteredIncidents;
    if (pageSize) {
      // For now, return all incidents since we're doing client-side pagination
      // In a real implementation, you might want to implement proper client-side pagination
      paginatedIncidents = filteredIncidents.slice(0, pageSize);
    }

    return {
      incidents: paginatedIncidents,
      lastDoc: undefined, // Not used for client-side pagination
      total: filteredIncidents.length
    };
  } catch (error) {
    console.error('Error fetching incidents:', error);
    throw new Error('Failed to fetch incidents');
  }
}

export async function getIncidentById(id: string, firestoreUser: User): Promise<Incident | null> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    console.log(`DEMO MODE: Fetching mock incident with id: ${id}`);
    await new Promise(resolve => setTimeout(resolve, 300)); // Simulate delay
    
    // Use imported mock data
    const mockIncidents = await loadMockData<Incident[]>('incidents', firestoreUser.language);
    const incident = mockIncidents.find(i => i.id === id) || null;
    return incident;
  }
  
  console.log('API Call: getIncidentById', { id, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Use a simpler approach - get all incidents for workspace and filter by ID
    const incidentsRef = collection(db, INCIDENTS_COLLECTION);
    const q = query(
      incidentsRef,
      where('workspace.id', '==', firestoreUser.workspace.id)
    );
    const snapshot = await getDocs(q);
    
    // Find the incident with matching ID
    const incidentDoc = snapshot.docs.find(doc => doc.id === id);
    
    if (!incidentDoc) {
      return null;
    }

    const incidentData = incidentDoc.data() as IncidentWithDates;
    const mappedIncident: IncidentWithDates = {
      ...incidentData,
      id: incidentDoc.id,
      reportedAt: incidentData.reportedAt ? formatFirestoreTimestamp(incidentData.reportedAt) ?? undefined : undefined,
      resolvedAt: incidentData.resolvedAt ? formatFirestoreTimestamp(incidentData.resolvedAt) ?? undefined : undefined
    };
    if (
      typeof mappedIncident.title === 'string' &&
      typeof mappedIncident.description === 'string' &&
      typeof mappedIncident.type === 'string' &&
      typeof mappedIncident.priority === 'string' &&
      typeof mappedIncident.status === 'string' &&
      mappedIncident.reportedBy && typeof mappedIncident.reportedBy.id === 'string' &&
      mappedIncident.reportedAt instanceof Date
    ) {
      return mappedIncident as Incident;
    }
    return null;
  } catch (error) {
    console.error('Error fetching incident by ID:', error);
    throw new Error('Failed to fetch incident');
  }
}

export async function addIncident(
  incident: Omit<Incident, 'id'>,
  firestoreUser: User
): Promise<string> {
  console.log('API Call: addIncident', { incident, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Validate required fields
    if (!incident.title?.trim()) {
      throw new Error('Incident title is required');
    }
    if (!incident.description?.trim()) {
      throw new Error('Incident description is required');
    }
    if (!incident.type) {
      throw new Error('Incident type is required');
    }
    if (!incident.priority) {
      throw new Error('Incident priority is required');
    }
    if (!incident.status) {
      throw new Error('Incident status is required');
    }
    if (!incident.reportedBy?.id) {
      throw new Error('Incident reporter is required');
    }
    if (!incident.reportedAt) {
      throw new Error('Incident report date is required');
    }

    const incidentData = addWorkspaceAndActivity(
      {
        ...incident,
        reportedAt: incident.reportedAt,
        resolvedAt: incident.resolvedAt,
        block: incident.block || undefined,
        apartmentUnit: incident.apartmentUnit || undefined,
        amenity: incident.amenity || undefined,
        attachments: incident.attachments || undefined,
        comments: incident.comments || undefined,
      },
      firestoreUser,
      'created'
    );

    const incidentsRef = collection(db, INCIDENTS_COLLECTION);
    const newIncidentRef = doc(incidentsRef);
    
    const finalData = removeUndefined({
      ...incidentData,
      id: newIncidentRef.id
    });

    console.log('Saving incident data:', finalData);

    await setDoc(newIncidentRef, finalData);

    return newIncidentRef.id;
  } catch (error: any) {
    console.error('Error adding incident:', error);
    
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied: You do not have access to create incidents');
    } else if (error.code === 'unavailable') {
      throw new Error('Service unavailable: Please try again later');
    } else if (error.code === 'invalid-argument') {
      throw new Error('Invalid data provided: Please check all required fields');
    } else if (error.message) {
      throw new Error(`Failed to add incident: ${error.message}`);
    } else {
      throw new Error('Failed to add incident: Unknown error occurred');
    }
  }
}

export async function updateIncident(
  id: string,
  data: Partial<Omit<Incident, 'id'>>,
  firestoreUser: User
): Promise<void> {
  console.log('API Call: updateIncident', { id, data, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    if (!id) {
      throw new Error('Incident ID is required for update');
    }

    // Get existing incident to create activity record
    const existingIncident = await getIncidentById(id, firestoreUser);
    if (!existingIncident) {
      throw new Error('Incident not found');
    }

    const activityRecord = createActivityRecord('updated', data, firestoreUser);

    const updateData = removeUndefined({
      ...data,
      reportedAt: data.reportedAt,
      resolvedAt: data.resolvedAt,
      block: data.block || undefined,
      apartmentUnit: data.apartmentUnit || undefined,
      amenity: data.amenity || undefined,
      attachments: data.attachments || undefined,
      comments: data.comments || undefined,
      activityRecords: [activityRecord]
    });

    console.log('Updating incident data:', updateData);

    // Use a simpler approach - get all incidents for workspace and find by ID
    const incidentsRef = collection(db, INCIDENTS_COLLECTION);
    const q = query(
      incidentsRef,
      where('workspace.id', '==', firestoreUser.workspace.id)
    );
    const snapshot = await getDocs(q);
    
    // Find the incident with matching ID
    const incidentDoc = snapshot.docs.find(doc => doc.id === id);
    
    if (!incidentDoc) {
      throw new Error('Incident not found');
    }

    await updateDoc(incidentDoc.ref, updateData);
  } catch (error: any) {
    console.error('Error updating incident:', error);
    
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied: You do not have access to update incidents');
    } else if (error.code === 'not-found') {
      throw new Error('Incident not found');
    } else if (error.message) {
      throw new Error(`Failed to update incident: ${error.message}`);
    } else {
      throw new Error('Failed to update incident: Unknown error occurred');
    }
  }
}

export async function deleteIncident(
  id: string,
  firestoreUser: User
): Promise<void> {
  console.log('API Call: deleteIncident', { id, firestoreUser });
  
  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    // Use a simpler approach - get all incidents for workspace and find by ID
    const incidentsRef = collection(db, INCIDENTS_COLLECTION);
    const q = query(
      incidentsRef,
      where('workspace.id', '==', firestoreUser.workspace.id)
    );
    const snapshot = await getDocs(q);
    
    // Find the incident with matching ID
    const incidentDoc = snapshot.docs.find(doc => doc.id === id);
    
    if (!incidentDoc) {
      throw new Error('Incident not found');
    }

    await deleteDoc(incidentDoc.ref);
  } catch (error) {
    console.error('Error deleting incident:', error);
    throw new Error('Failed to delete incident');
  }
}

export async function getIncidentsByStatus(status: IncidentStatus, firestoreUser: User): Promise<Incident[]> {
  console.log('API Call: getIncidentsByStatus', { status, firestoreUser });
  
  const { incidents } = await getIncidents({ status, firestoreUser });
  return incidents;
}

export async function getIncidentsByType(type: IncidentType, firestoreUser: User): Promise<Incident[]> {
  console.log('API Call: getIncidentsByType', { type, firestoreUser });
  
  const { incidents } = await getIncidents({ type, firestoreUser });
  return incidents;
}

export async function getIncidentsByPriority(priority: IncidentPriority, firestoreUser: User): Promise<Incident[]> {
  console.log('API Call: getIncidentsByPriority', { priority, firestoreUser });
  
  const { incidents } = await getIncidents({ priority, firestoreUser });
  return incidents;
} 